import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLang } from '@/context/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Lock, LogOut, Loader2, Mail, Inbox } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'cea-admin-token';

export default function AdminPage() {
  const { t } = useLang();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchContacts = useCallback(async (tk) => {
    setFetching(true);
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${tk}` } });
      setItems(res.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(''); }
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => { if (token) fetchContacts(token); }, [token, fetchContacts]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError(t.admin.wrong);
    } finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(''); setItems([]); };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#040f0c] text-white flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl border border-[var(--cea-border)] bg-[#0b241b] p-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--cea-accent)] text-[#040f0c] cea-glow"><Lock size={20} /></span>
            <div>
              <h1 className="font-display font-medium text-xl leading-none">{t.admin.title}</h1>
              <p className="text-xs text-white/50 mt-1">{t.admin.subtitle}</p>
            </div>
          </div>
          <form onSubmit={login} className="space-y-3">
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.admin.password}
              className="w-full rounded-xl border border-[var(--cea-border)] bg-[#040f0c] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cea-accent)]"
            />
            {error && <p className="text-xs text-red-400" data-testid="admin-login-error">{error}</p>}
            <button data-testid="admin-login-submit" disabled={loading} className="glow-btn w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--cea-accent)] disabled:opacity-60">
              {loading ? <Loader2 size={18} className="animate-spin" /> : t.admin.login}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#040f0c] text-white">
      <header className="border-b border-[var(--cea-border)] bg-[#040f0c]/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <h1 className="font-display font-medium tracking-wide">{t.admin.title}</h1>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <button onClick={logout} data-testid="admin-logout" className="glow-btn inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold uppercase tracking-wider text-[var(--cea-accent)]"><LogOut size={16} /> {t.admin.logout}</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-5 text-white/70">
          <Inbox size={18} className="text-[var(--cea-accent)]" />
          <span data-testid="admin-total"><span className="font-bold text-white">{items.length}</span> {t.admin.total}</span>
        </div>

        {fetching ? (
          <div className="flex items-center gap-2 text-white/60"><Loader2 size={18} className="animate-spin" /> {t.admin.loading}</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[var(--cea-border)] p-12 text-center text-white/55" data-testid="admin-empty">{t.admin.empty}</div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[var(--cea-border)]">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="bg-[#0b241b] text-left text-white/60 text-xs uppercase tracking-wide">
                  <th className="px-4 py-3">{t.admin.colDate}</th>
                  <th className="px-4 py-3">{t.admin.colName}</th>
                  <th className="px-4 py-3">{t.admin.colEmail}</th>
                  <th className="px-4 py-3">{t.admin.colSubject}</th>
                  <th className="px-4 py-3">{t.admin.colMessage}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} onClick={() => setSelected(it)} data-testid={`admin-row-${it.id}`} className="border-t border-white/5 hover:bg-[#0b241b] cursor-pointer">
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmt(it.created_at)}</td>
                    <td className="px-4 py-3 font-semibold">{it.name}</td>
                    <td className="px-4 py-3 text-[var(--cea-accent)]"><a href={`mailto:${it.email}`} className="inline-flex items-center gap-1"><Mail size={12} />{it.email}</a></td>
                    <td className="px-4 py-3">{it.subject}</td>
                    <td className="px-4 py-3 text-white/70 max-w-xs truncate">{it.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-[var(--cea-overlay)] backdrop-blur-sm" />
          <div className="relative z-10 w-full max-w-lg rounded-2xl border border-[var(--cea-border-strong)] bg-[#0b241b] p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs text-white/45">{fmt(selected.created_at)}</p>
            <h3 className="mt-1 font-display font-medium text-xl">{selected.subject}</h3>
            <p className="mt-1 text-sm text-[var(--cea-accent)]">{selected.name} · {selected.email}</p>
            <p className="mt-4 text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
            <button onClick={() => setSelected(null)} className="mt-6 rounded-xl border border-[var(--cea-border)] px-4 py-2 text-sm font-semibold hover:bg-[#12382c]">{t.modal.close}</button>
          </div>
        </div>
      )}
    </div>
  );
}
